import { useQuery } from '@tanstack/react-query';

import { Barra, Carregando } from '@/componentes/Esqueleto';
import { explicarFalha } from '@/lib/pedido';
import { api as apiDePessoas } from '@/modulos/pessoas/api';
import { motivoEscrito } from '@/modulos/pessoas/pessoas';
import { useSessao } from '@/modulos/sessao/consultas';

/**
 * A CONFERÊNCIA DE DOCUMENTOS ANTES DE ALOCAR (módulo 16, ferramenta 1; 0066).
 *
 * O v6 escreve o risco do módulo: "alocar sem conferir". A regra do impedimento
 * existia desde 01/10, e o código dizia que a tela de alocação a consultava —
 * nenhuma tela consultava. Agora, escolhida a obra (e o contrato, se houver), a
 * tela mostra o que ficaria pendente: o documento exigido que a máquina, ou
 * quem pode operá-la, não tem, está vencido ou sem data.
 *
 * NÃO BARRA: a alocação continua permitida, e a frase diz isso. Virar recusa é
 * decisão do dono, registrada como pendente. Quem não tem `impedimento.ler`
 * não vê o bloco — e aloca como sempre alocou.
 */
export function ConferenciaDeDocumentos({
  equipamentoId,
  obraId,
  contratoId,
}: {
  readonly equipamentoId: string;
  readonly obraId: string;
  readonly contratoId: string | null;
}) {
  const sessao = useSessao();
  const pode = (sessao.data?.permissoes ?? []).includes('impedimento.ler');
  const conferencia = useQuery({
    queryKey: ['pessoas', 'impedimentos', 'alocacao', equipamentoId, obraId, contratoId],
    queryFn: () => apiDePessoas.impedimentosDaAlocacao(equipamentoId, obraId, contratoId),
    enabled: pode && obraId !== '',
  });

  if (!pode || obraId === '') return null;

  if (conferencia.isPending) {
    return (
      <Carregando oQue="Conferindo os documentos que a obra exige" className="flex flex-col gap-2 rounded-controle border border-borda-sutil p-3">
        <Barra className="h-3 w-1/2" />
        <Barra className="h-3 w-4/5" />
      </Carregando>
    );
  }

  if (conferencia.isError) {
    return (
      <p className="txt-apoio text-tinta-secundaria">
        {`Não foi possível conferir os documentos agora. ${explicarFalha(conferencia.error).comoResolver} A alocação não depende desta conferência.`}
      </p>
    );
  }

  const pendencias = conferencia.data.impedimentos;
  if (pendencias.length === 0) {
    return (
      <p className="txt-apoio text-tinta-secundaria">
        Nenhuma pendência de documento: a máquina e quem pode operá-la têm o que esta obra e este contrato exigem.
      </p>
    );
  }

  return (
    <div role="status" className="flex flex-col gap-2 rounded-controle border border-atencao bg-atencao-fundo p-3">
      <p className="txt-corpo font-semibold text-tinta">
        {pendencias.length === 1
          ? 'Esta alocação fica com 1 pendência de documento'
          : `Esta alocação fica com ${String(pendencias.length)} pendências de documento`}
      </p>
      <ul className="flex flex-col gap-1">
        {pendencias.slice(0, 6).map((p) => (
          <li key={`${p.sobre}-${p.alvoId}-${p.tipoCodigo}`} className="txt-apoio text-tinta">
            {`${p.alvo} · ${p.tipo}: ${motivoEscrito(p.motivo).toLocaleLowerCase('pt-BR')}${p.obrigatorio ? '' : ' (recomendado)'}`}
          </li>
        ))}
        {pendencias.length > 6 ? (
          <li className="txt-apoio text-tinta-secundaria">{`e mais ${String(pendencias.length - 6)}, em Pessoas e documentos`}</li>
        ) : null}
      </ul>
      <p className="txt-apoio text-tinta-secundaria">{conferencia.data.aviso}</p>
    </div>
  );
}
